import React, { useContext, useEffect } from 'react';
import { ChatbotContext } from '../contexts/ChatbotContext';
import { Avatar, AvatarFallback, AvatarImage } from './ui/Avatar';
import { useToast } from './ui/toast/UseToast';
import { APIContext } from '../contexts/APIContext';
import { ChatHistory, Response, ResponseWithSource } from '../types';
import { ToastAction } from './ui/toast/Toast';
import { capitalizeEveryWord, trackUserClicks, getUserEnterpriseId, getValidToken } from '../utils';
import { Reorder, motion } from 'framer-motion';
import { StoryBuilderCard } from './StoryBuilderCard';
import { useMsal } from '@azure/msal-react';
import { SessionContext } from '../contexts/SessionContext';

const buildGenerateRequest = (
  storyResponses: ResponseWithSource[],
  chatPromptResponseHistory: ChatHistory[],
  accounts: any,
  SessionUId: string,
  accessToken: any
) => {
  const stories: {
    responseId: string;
    title: string;
    response: string;
    source: string;
    SelectedProjects: string[] | null;
  }[] = storyResponses.map((storyResponse) => {
    const project = chatPromptResponseHistory.find((history) => history.response === storyResponse.response);
    const story = {
      // Capitalize the first letter of every word in the market
      title: `${capitalizeEveryWord(storyResponse.market)} Market Research`,
      response: storyResponse.response,
      responseId: storyResponse.responseId,
      source: storyResponse.source,
      SelectedProjects: project ? project.SelectedProjects : storyResponse.projectName,
      genArcPromptAnalyticsUId: storyResponse.genArcPromptAnalyticsUId,
    };
    return story;
  });
  return {
    function: 'generate_report',
    userId: localStorage.getItem('gamma-userid') || crypto.randomUUID(),
    stories: stories,
    EnterpriseId: getUserEnterpriseId(accounts),
    SessionUId: SessionUId,
    authorization: `Bearer ${accessToken}`,
  };
};

export const StoryBuilder = (props: any) => {
  // Get the storyResponses array from the chatbot context
  const { storyResponses, setStoryResponses, chatPromptResponseHistory } = useContext(ChatbotContext);
  const {
    websocketConnectionRef: { current: websocket },
    exportedStoryURL,
    setExportedStoryURL,
    exportStoryInProgress,
    setExportStoryInProgress,
    userDetails,
    chatSessionId,
    WBUId,
    AppName,
    accounts,
  } = useContext(APIContext);
  // Use toast() to display a toast message
  const { toast } = useToast();
  // const { accounts } = useMsal();
  const isAuthenticated = accounts.length > 0;
  const username = isAuthenticated ? accounts[0].username : ' ';

  // useEffect to watch for the exportedStoryURL to be set
  useEffect(() => {
    if (exportedStoryURL) {
      // Open the URL in a new tab
      if (window.self === window.top) {
        window.open(exportedStoryURL, '_self');
      } else {
        window.open(exportedStoryURL, '_blank');
      }
      // Reset the exportedStoryURL and exportStoryInProgress
      setExportedStoryURL('');
      setExportStoryInProgress(false);
      // Display the success toast message
      toast({
        status: 'success',
        description: 'Your PowerPoint has been generated!',
        action: (
          <ToastAction
            altText="View Storybuilder"
            onClick={() => window.open(exportedStoryURL, '_blank')}
            className="flex items-center justify-center w-[max-content] h-full font-graphik text-[16px] font-semibold tracking-[-0.32px] text-gamma-purple"
          >
            Download hasn&apos;t started? Click here
          </ToastAction>
        ),
      });
    }
  }, [exportedStoryURL]);

  // useEffect to watch for the exportStoryInProgress to be set
  useEffect(() => {
    if (exportStoryInProgress) {
      // Reenable the generate button after 15 seconds in case the BE doesn't respond or call fails
      setTimeout(() => {
        setExportStoryInProgress(false);
      }, 15000);
    }
  }, [exportStoryInProgress]);

  // Function to initiate exporting the story
  const exportStory = async () => {
    const token = await getValidToken();
    setExportStoryInProgress(true);
    // Build the request
    const request = buildGenerateRequest(storyResponses, chatPromptResponseHistory, accounts, chatSessionId, token);
    console.log('Generate Report Request: ', request);
    // Send the request to the backend - websocket will never be null here
    websocket!.send('SendMessage', request);
  };

  // Function to delete the story response from the story builder
  const deleteStoryResponse = (genArcPromptAnalyticsUId: string) => {
    const updatedStoryResponse = storyResponses.filter(
      (resp) => resp.genArcPromptAnalyticsUId !== genArcPromptAnalyticsUId
    );
    setStoryResponses(updatedStoryResponse);
  };

  return (
    <>
      {/* If there are story responses, display them and if not, then show blank message */}
      {storyResponses.length > 0 ? (
        <>
          <div className="flex flex-col justify-between">
            <div>
              <motion.div className="storybuilder-container flex flex-col w-full h-full gap-6 p-4" layoutScroll>
                <Reorder.Group
                  className="storybuilder-card-container flex flex-col w-full gap-4"
                  axis="y"
                  values={storyResponses}
                  onReorder={setStoryResponses}
                >
                  {/* Map through the storyResponse array and display each response */}
                  {storyResponses.map((storyResponse) => {
                    return (
                      <StoryBuilderCard
                        key={storyResponse.genArcPromptAnalyticsUId}
                        storyResponse={storyResponse}
                        deleteStoryResponse={deleteStoryResponse}
                      />
                    );
                  })}
                </Reorder.Group>
              </motion.div>
            </div>
            <div className="flex items-center justify-center min-h-[12%] shadow-[0_-4px_16px_0_rgba(0,0,0,0.06)]">
              <button
                type="button"
                onClick={() => {
                  exportStory();
                  (async () => {
                    const token = await getValidToken();
                    trackUserClicks(
                      websocket,
                      'Download StoryBuilderPPT',
                      userDetails,
                      chatSessionId,
                      WBUId,
                      AppName,
                      accounts,
                      token
                    );
                  })();
                  // Display the loading toast message
                  toast({
                    status: 'loading',
                    description: 'Your PowerPoint is being generated...',
                  });
                }}
                disabled={exportStoryInProgress}
                className="flex justify-center min-w-[108px] border border-solid border-[#000] p-[12px_16px_12px_16px] font-graphik font-normal not-italic text-base leading-[14px] hover:text-[#460073] hover:border-[#460073] transition duration-300 ease-in-out"
              >
                <img src="/icons/download-new.svg" className="mr-1.5 w-[14px] h-[14px] text-[#50d71e]"></img>
                Generate PowerPoint
              </button>
            </div>
          </div>
        </>
      ) : (
        <div className="no-story-container flex flex-col w-full items-center justify-center gap-6 h-[75vh]">
          <div className="items-center justify-center w-[100px] h-[100px]">
            <img src="/icons/bot-new.svg" className="w-80 h-87"></img>
          </div>
          <div className="text-wrapper flex flex-col">
            <p className="text-base leading-[16px] font-normal text-black text-opacity-[0.87] text-center font-graphik">
              Your story is still being told
            </p>
            <p className="text-base font-normal leading-[17px] text-black text-opacity-[0.87] text-center font-graphik w-[192px]">
              Responses you&apos;ve added to your story will show here. Click on the plus icon below a response in the
              chat window to add it to your story.
            </p>
          </div>
        </div>
      )}
    </>
  );
};
